import { type Contact, type OrgModelRecord, orgModelsService } from "@/app/providers/services";
import { OrgModelGraph } from "@/features/org-models";
import LoadingSpinner from "@imminently/decisively-ui/LoadingSpinner";
import SplitPane from "@imminently/decisively-ui/SplitPane";
import { useEffect, useMemo, useState } from "react";

const SECTION_CLASS = "flex min-h-0 flex-1 flex-col overflow-hidden border-b border-[var(--border)] last:border-b-0";
const SECTION_HEADER_CLASS = "flex items-center justify-between gap-2 border-b border-[var(--border)] px-3 py-2";
const TABLE_WRAP_CLASS = "min-h-0 flex-1 overflow-auto";
const TABLE_CLASS = "w-full table-fixed border-collapse text-left text-xs";
const TH_CLASS = "sticky top-0 z-10 bg-[var(--card)] px-3 py-2 font-semibold text-[var(--muted-foreground)]";
const TD_CLASS = "min-w-0 truncate border-b border-[var(--border)] px-3 py-2 font-mono";
const ROW_CLASS = "cursor-pointer hover:bg-[var(--secondary-hover)]";
const ROW_SELECTED_CLASS = "bg-[var(--secondary)]";
const EMPTY_CLASS = "flex h-full items-center justify-center px-4 text-sm text-[var(--muted-foreground)]";

const formatOrgModelSummary = (record: OrgModelRecord) => {
  if (record.type === "hierarchySummary") return `${record.subjectType}:${record.subjectId}`;
  if (record.type === "hierarchyIssue") return record.message || record.issueType || "issue";
  return `${record.subjectType}:${record.subjectId} -> ${record.relatedId || "-"}`;
};

type OrgModelContactListProps = {
  title: string;
  records: OrgModelRecord[];
  total?: number;
  loading: boolean;
  selectedRecordId?: string | null;
  onSelect: (record: OrgModelRecord) => void;
};

const OrgModelContactList = ({
  title,
  records,
  total,
  loading,
  selectedRecordId,
  onSelect,
}: OrgModelContactListProps) => {
  return (
    <section className={SECTION_CLASS}>
      <div className={SECTION_HEADER_CLASS}>
        <h3 className="text-sm font-semibold text-[var(--foreground)]">{title}</h3>
        <span className="text-xs text-[var(--muted-foreground)]">{total ?? records.length}</span>
      </div>
      <div className={TABLE_WRAP_CLASS}>
        {loading ? (
          <div className="flex h-full items-center justify-center">
            <LoadingSpinner size="sm" />
          </div>
        ) : records.length === 0 ? (
          <div className={EMPTY_CLASS}>No org model records found</div>
        ) : (
          <table className={TABLE_CLASS}>
            <thead>
              <tr>
                <th className={TH_CLASS}>Type</th>
                <th className={TH_CLASS}>Relationship</th>
              </tr>
            </thead>
            <tbody>
              {records.map((record) => (
                <tr
                  key={record.id}
                  className={[ROW_CLASS, record.id === selectedRecordId ? ROW_SELECTED_CLASS : undefined]
                    .filter(Boolean)
                    .join(" ")}
                  onClick={() => onSelect(record)}
                >
                  <td className={TD_CLASS}>{record.type}</td>
                  <td className={TD_CLASS}>{formatOrgModelSummary(record)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </section>
  );
};

export const ContactOrgModelsTab = ({ contact }: { contact: Contact }) => {
  const [selectedRecord, setSelectedRecord] = useState<OrgModelRecord | null>(null);

  useEffect(() => {
    setSelectedRecord(null);
  }, [contact.id]);

  const baseFilter = useMemo(
    () => ({
      $orderBy: "lastModified:desc",
      $limit: 100,
    }),
    [],
  );

  const {
    data: subjectRecords = [],
    total: subjectTotal,
    query: subjectQuery,
  } = orgModelsService.useResources({
    filter: {
      ...baseFilter,
      subjectId: contact.id,
    },
  });

  const {
    data: relatedRecords = [],
    total: relatedTotal,
    query: relatedQuery,
  } = orgModelsService.useResources({
    filter: {
      ...baseFilter,
      relatedId: contact.id,
    },
  });

  return (
    <div className="flex h-full min-h-0 w-full overflow-hidden">
      <SplitPane
        direction="horizontal"
        gutterSize={6}
      >
        <SplitPane.Pane
          minSize={280}
          preferredSize="34%"
        >
          <div className="flex h-full min-h-0 flex-col overflow-hidden border-r border-[var(--border)]">
            <OrgModelContactList
              title="Subject ID"
              records={subjectRecords as OrgModelRecord[]}
              total={subjectTotal}
              loading={subjectQuery.isLoading || subjectQuery.isFetching}
              selectedRecordId={selectedRecord?.id}
              onSelect={setSelectedRecord}
            />
            <OrgModelContactList
              title="Related ID"
              records={relatedRecords as OrgModelRecord[]}
              total={relatedTotal}
              loading={relatedQuery.isLoading || relatedQuery.isFetching}
              selectedRecordId={selectedRecord?.id}
              onSelect={setSelectedRecord}
            />
          </div>
        </SplitPane.Pane>
        <SplitPane.Pane minSize={420}>
          <OrgModelGraph
            selectedRecord={selectedRecord}
            emptyMessage="Select an org model record for this contact"
            onClear={() => setSelectedRecord(null)}
          />
        </SplitPane.Pane>
      </SplitPane>
    </div>
  );
};
